/**
 * The single init path.
 *
 * LESSON PINNED AT THE SCENE (Journal post-mortem §12, §9): three boot variants —
 * cold start, resume from the task switcher, and return from an auth redirect —
 * each grew their own initialisation sequence. They drifted, and the bugs that
 * followed only reproduced on one of the three, which is the most expensive shape
 * a bug can have. Every variant funnels through `runInit` here.
 *
 * Two guarantees:
 *  1. **In-flight guard.** Concurrent callers share one promise. A resume event
 *     firing while a cold boot is still running must not seed the database twice.
 *  2. **Explicit ordering.** Steps declare their dependencies by name; the runner
 *     refuses a step whose dependency has not completed, rather than racing.
 *     Idempotency on the server is still required — this is belt, not braces.
 */
let inFlight = null;
/**
 * Run the boot sequence exactly once at a time. Concurrent callers get the same
 * promise; the guard is cleared when it settles, so a later resume can re-init.
 */
export function runInit(steps, context, options = {}) {
    if (inFlight)
        return inFlight;
    inFlight = execute(steps, context, options).finally(() => {
        inFlight = null;
    });
    return inFlight;
}
/** Test seam. Never call from application code. */
export function resetInitGuardForTests() {
    inFlight = null;
}
async function execute(steps, context, options) {
    const required = new Set(options.required ?? []);
    const result = { completed: [], failed: [], skipped: [], ok: true };
    const done = new Set();
    for (const step of steps) {
        if (step.skipOn?.includes(context.kind)) {
            result.skipped.push(step.name);
            continue;
        }
        const missing = (step.needs ?? []).filter((dep) => !done.has(dep));
        if (missing.length > 0) {
            // Not a race to be retried — a declared dependency did not complete.
            // Fail loudly rather than running a step against half-built state.
            const error = new Error(`init step "${step.name}" needs [${missing.join(', ')}], which did not complete`);
            context.onFailure(error, step.name);
            result.failed.push({ step: step.name, error });
            if (required.has(step.name))
                result.ok = false;
            continue;
        }
        try {
            await step.run(context);
            done.add(step.name);
            result.completed.push(step.name);
        }
        catch (error) {
            context.onFailure(error, step.name);
            result.failed.push({ step: step.name, error });
            if (required.has(step.name))
                result.ok = false;
        }
    }
    return result;
}
//# sourceMappingURL=init.js.map